import {TriggerParams} from './TriggerParams';
import {Visibility} from './Visibility';

export enum MessageFromParentType {
  // An update the widget's visibility has changed.
  // Since the parent owns the iframe, it is the authority on the
  // visibility state. This is the message sent to our iframe to let it know
  // when it should update internal visibility state.
  VisibilityChange = 'visibility-change',

  // An update that the widget's parent document visibility has changed.
  // E.g. when a user changes tabs.
  DocumentVisibilityChange = 'document-visibility-change',

  // A response to the request-analytics-event-id message, returning the
  // parent's analytics event id.
  AnalyticsEventId = 'analytics-event-id',

  // A response to the request-action message.
  RequestActionResponse = 'request-action-response',

  // Ask the iframe to send if it is initialized. If the iframe is not
  // initialized, nothing will get sent. If it is, the iframe will send
  // the initialized message.
  CheckInitialized = 'check-initialized',

  // Ask the iframe to get new smart suggestions from the assistant
  // if there is a conversation active.
  GetSmartSuggestions = 'get-smart-suggestions',

  // For the experimental iframeles agent escalation, it hands off
  // conversation to the iframe on escalation
  ContextHandoff = 'context-handoff',
}

export interface BaseMessageFromParent {
  audience?: string;
  entryPoint?: string;
}

export interface VisibilityChangeMessage extends BaseMessageFromParent {
  type: MessageFromParentType.VisibilityChange;
  data: {
    visibility: Visibility;
    triggerParams?: TriggerParams;
  };
}

export interface DocumentVisibilityChangeMessage extends BaseMessageFromParent {
  type: MessageFromParentType.DocumentVisibilityChange;
  data: {
    documentVisibility: DocumentVisibilityState;
  };
}

export interface AnalyticsEventIdMessage extends BaseMessageFromParent {
  type: MessageFromParentType.AnalyticsEventId;
  data: {
    analyticsEventId: string | null;
  };
}

export interface RequestActionResponseMessage extends BaseMessageFromParent {
  type: MessageFromParentType.RequestActionResponse;
  data: Record<string, unknown>;
  nonce?: string;
}

export interface CheckInitializedMessage extends BaseMessageFromParent {
  type: MessageFromParentType.CheckInitialized;
}

export interface GetSmartSuggestionsMessage extends BaseMessageFromParent {
  type: MessageFromParentType.GetSmartSuggestions;
}

export interface ContextHandoff extends BaseMessageFromParent {
  type: MessageFromParentType.ContextHandoff;
  context: Record<string, unknown>;
}

export type MessageFromParent =
  | VisibilityChangeMessage
  | DocumentVisibilityChangeMessage
  | AnalyticsEventIdMessage
  | RequestActionResponseMessage
  | CheckInitializedMessage
  | GetSmartSuggestionsMessage
  | ContextHandoff;
